// Contexto compartilhado entre `App.tsx` (onde o `MobileBottomBar` é renderizado
// fora das `<Routes>`) e `Home.tsx` (onde vivem os filtros e a lista de
// acompanhantes). Permite que o bottom bar abra o drawer de filtros e a faixa
// de mapa sem precisar levantar estado até o topo da árvore ou expor props
// through route components.
//
// Split into two contexts to avoid re-renders:
// - MobileOverlayControlsContext: open/close state + functions (used by MobileBottomBar)
// - MobileOverlayDataContext: filterProps + mapProps (used by MobileFiltersDrawer, MobileMapStrip)

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// ===== CONTROLS CONTEXT (for MobileBottomBar) =====
interface MobileOverlayControls {
  filtersOpen: boolean;
  mapOpen: boolean;
  openFilters: () => void;
  closeFilters: () => void;
  openMap: () => void;
  closeMap: () => void;
}

const MobileOverlayControlsContext = createContext<MobileOverlayControls | null>(null);

export function useMobileOverlayControls(): MobileOverlayControls {
  const ctx = useContext(MobileOverlayControlsContext);
  if (!ctx) {
    throw new Error("useMobileOverlayControls must be used within MobileOverlayProvider");
  }
  return ctx;
}

// ===== DATA CONTEXT (for MobileFiltersDrawer, MobileMapStrip) =====
export interface FilterProps {
  q: string;
  onQChange: (v: string) => void;
  category: string;
  categories: Array<{ name: string; slug: string; icon?: string }>;
  onCategoryChange: (v: string) => void;
  subcategory: string;
  subcategories: Array<{ name: string; slug: string }>;
  onSubcategoryChange: (v: string) => void;
  location: { state: string; city: string; neighborhood: string };
  onLocationChange: (next: { state?: string; city?: string; neighborhood?: string }) => void;
  attributeCategories: Array<{
    id: string;
    name: string;
    options: Array<{ label: string; value: string }>;
  }>;
  selectedAttributes: Record<string, string[]>;
  setSelectedAttributes: (
    updater: Record<string, string[]> | ((prev: Record<string, string[]>) => Record<string, string[]>),
  ) => void;
  serviceOptions: Array<{ id: string; name: string }>;
  selectedService: string[];
  setSelectedService: (v: string[]) => void;
  onlineOnly: boolean;
  setOnlineOnly: (v: boolean) => void;
  sortMode: string;
  setSortMode: (v: string) => void;
  maxKm: number | null;
  setMaxKm: (v: number | null) => void;
  userLocation: { lat: number; lng: number } | null;
  clearFilters: () => void;
  clearUserLocation: () => void;
  hasActiveFilters: boolean;
  onApply: () => void;
}

export interface MapProps {
  listings: Array<Record<string, unknown>>;
  userLocation: { lat: number; lng: number } | null;
  maxKm: number | null;
  filterState?: string;
  filterCity?: string;
}

interface MobileOverlayData {
  filterProps: FilterProps | null;
  mapProps: MapProps | null;
  setFilterProps: (p: FilterProps | null) => void;
  setMapProps: (p: MapProps | null) => void;
}

const MobileOverlayDataContext = createContext<MobileOverlayData | null>(null);

export function useMobileOverlayData(): MobileOverlayData {
  const ctx = useContext(MobileOverlayDataContext);
  if (!ctx) {
    throw new Error("useMobileOverlayData must be used within MobileOverlayProvider");
  }
  return ctx;
}

// ===== PROVIDER =====
export function MobileOverlayProvider({ children }: { children: ReactNode }) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [filterProps, setFilterProps] = useState<FilterProps | null>(null);
  const [mapProps, setMapProps] = useState<MapProps | null>(null);

  const openFilters = useCallback(() => setFiltersOpen(true), []);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  const openMap = useCallback(() => setMapOpen(true), []);
  const closeMap = useCallback(() => setMapOpen(false), []);

  // Controls context value - stable unless open state changes
  const controlsValue = useMemo<MobileOverlayControls>(
    () => ({
      filtersOpen,
      mapOpen,
      openFilters,
      closeFilters,
      openMap,
      closeMap,
    }),
    [filtersOpen, mapOpen],
  );

  // Data context value - stable unless data changes
  const dataValue = useMemo<MobileOverlayData>(
    () => ({
      filterProps,
      mapProps,
      setFilterProps,
      setMapProps,
    }),
    [filterProps, mapProps],
  );

  return (
    <MobileOverlayControlsContext.Provider value={controlsValue}>
      <MobileOverlayDataContext.Provider value={dataValue}>
        {children}
      </MobileOverlayDataContext.Provider>
    </MobileOverlayControlsContext.Provider>
  );
}

// ===== BACKWARDS COMPAT =====
// For any existing code using useMobileOverlay()
interface MobileOverlayValue {
  filtersOpen: boolean;
  mapOpen: boolean;
  filterProps: FilterProps | null;
  mapProps: MapProps | null;
  openFilters: () => void;
  closeFilters: () => void;
  openMap: () => void;
  closeMap: () => void;
  setFilterProps: (p: FilterProps | null) => void;
  setMapProps: (p: MapProps | null) => void;
}

export function useMobileOverlay(): MobileOverlayValue {
  const controls = useMobileOverlayControls();
  const data = useMobileOverlayData();
  return {
    ...controls,
    ...data,
  };
}